import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetUnitOfMeasures } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import { IUnitOfMeasure, UnitOfMeasure } from '../../../@types/model/masterData/unitOfMeasure/unitOfMeasure';
import { UnitOfMeasureFormValues, IUnitOfMeasureFormValues } from '../../../@types/model/masterData/unitOfMeasure/unitOfMeasureFormValues';
import UnitOfMeasureHttpService from '../../../services/http/masterData/unitOfMeasureHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import UnitOfMeasureForm from './form/UnitOfMeasureForm';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useLocation } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { setUnitOfMeasureMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';

const UnitOfMeasureScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const unitOfMeasures = useAppSelector(x => x.masterData.unitOfMeasures);
    const unitOfMeasureTypes = useAppSelector(x => x.masterData.unitOfMeasureTypes);
    const auth = useAppSelector(x => x.auth);
    const pathname = useLocation()?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedUnitOfMeasure, setSelectedUnitOfMeasure] = useState<IUnitOfMeasure>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingUnitOfMeasure, setDeletingUnitOfMeasure] = useState<IUnitOfMeasure>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const res = await UnitOfMeasureHttpService.getUnitOfMeasureData();
                    dispatch(dataSetUnitOfMeasures(res.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading unit of measures.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const openDialog = () => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setSelectedUnitOfMeasure(undefined);
        setFormGuid(undefined);
    };

    const editUnitOfMeasure = (unitOfMeasure : IUnitOfMeasure) => {
        setSelectedUnitOfMeasure(unitOfMeasure);
        setFormGuid(unitOfMeasure.guid);
        openDialog();
    };

    const getUnitOfMeasureTypeName = useCallback((unitOfMeasureTypeId : number) => {
        const unitOfMeasureType = unitOfMeasureTypes?.find(x => x.id === unitOfMeasureTypeId);
        return unitOfMeasureType?.name ?? '';
    }, [unitOfMeasureTypes]);

    const onSubmit = async (values : IUnitOfMeasureFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        payload.guid = formGuid ?? uuidv4();

        try {
            const res = await UnitOfMeasureHttpService.addOrUpdateUnitOfMeasure(UnitOfMeasure(payload));
            const updatedList = upsertArrayElement(unitOfMeasures, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetUnitOfMeasures(updatedList));
            await setUnitOfMeasureMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedUnitOfMeasure ? 'Unit of measure updated successfully.' : 'Unit of measure added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating unit of measure data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = (unitOfMeasure : IUnitOfMeasure) => {
        setDeletingUnitOfMeasure(unitOfMeasure);
        setIsDeletePopupOpen(true);
    };

    const closeDeleteConfirmationPopup = () => {
        setDeletingUnitOfMeasure(undefined);
        setIsDeletePopupOpen(false);
    };

    const removeUnitOfMeasure = async () => {
        if (!deletingUnitOfMeasure) return;
        const updatedUnitOfMeasure = { ...deletingUnitOfMeasure, isActive: false };
        setIsLoading(true);
        try {
            const res = await UnitOfMeasureHttpService.deleteUnitOfMeasure(updatedUnitOfMeasure.id);
            const updatedList = upsertArrayElement(unitOfMeasures, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetUnitOfMeasures(updatedList));
            await setUnitOfMeasureMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Unit of measure deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting unit of measure.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return UnitOfMeasureFormValues(selectedUnitOfMeasure, unitOfMeasureTypes);
        }
    }, [selectedUnitOfMeasure, unitOfMeasureTypes, isDialogOpen]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(r => pathname.includes(r.url) && r.isActive && r.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Unit of Measure'
                    isEdit={!!selectedUnitOfMeasure}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    maxWidth={'md'}
                    fullWidth
                    onClose={closeDialog}>
                    <UnitOfMeasureForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<IUnitOfMeasure>
                        enableAdding={hasEditingRight}
                        addFunction={openDialog}
                        editFunction={editUnitOfMeasure}
                        enableEditing={hasEditingRight}
                        enableDeleting={u => u.isActive && hasEditingRight}
                        deleteFunction={openDeleteConfirmationPopup}
                        enableSorting
                        enableFiltering
                        enablePagination
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Unit of Measure Type', field: 'typeId', formatFunction: getUnitOfMeasureTypeName, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        fitWidthToPage
                        rows={unitOfMeasures}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            <ConfirmationDialog
                title='Delete Unit of Measure'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this unit of measure?'
                onAccept={removeUnitOfMeasure}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default UnitOfMeasureScreen;
